/**
 * Chart configuration utilities — axis normalization, default selection,
 * resolved config computation, and chart type switching helpers.
 *
 * The backend may send x_axis as a single object or an array. These helpers
 * normalize that and produce the resolved ChartConfig that ByodChart /
 * buildCategoricalSeries / buildProportionSeries consume (single x_axis).
 */

import {CHART_KIND_GROUPS} from '@spaced-out/ui-design-system/lib/components/Charts/UniversalChart';
import type {ChartKind} from '@spaced-out/ui-design-system/lib/components/Charts/UniversalChart';

import type {
  ChartAxis,
  ChartConfig,
  ChartSeries,
  ChartStyle,
} from 'src/types/byod';

// ---------------------------------------------------------------------------
// Kind groups — sourced from the design system so BYOD stays in sync with
// UniversalChart's compatibility matrix.
// ---------------------------------------------------------------------------

const CATEGORICAL_KIND_KEYS: ReadonlyArray<ChartKind> =
  CHART_KIND_GROUPS.CATEGORICAL.map((opt) => opt.key);
const PROPORTION_KIND_KEYS: ReadonlyArray<ChartKind> =
  CHART_KIND_GROUPS.PROPORTION.map((opt) => opt.key);
const FLOW_KIND_KEYS: ReadonlyArray<ChartKind> = CHART_KIND_GROUPS.FLOW.map(
  (opt) => opt.key,
);

export function isCategoricalKind(style: ChartStyle): boolean {
  return CATEGORICAL_KIND_KEYS.includes(style);
}

export function isProportionKind(style: ChartStyle): boolean {
  return PROPORTION_KIND_KEYS.includes(style);
}

export function isFlowKind(style: ChartStyle): boolean {
  return FLOW_KIND_KEYS.includes(style);
}

export function getKindsInGroup(style: ChartStyle): ReadonlyArray<ChartStyle> {
  if (isProportionKind(style)) {
    return PROPORTION_KIND_KEYS as ReadonlyArray<ChartStyle>;
  }
  if (isFlowKind(style)) {
    return FLOW_KIND_KEYS as ReadonlyArray<ChartStyle>;
  }
  return CATEGORICAL_KIND_KEYS as ReadonlyArray<ChartStyle>;
}

// ---------------------------------------------------------------------------
// Non-modifiable chart styles — no axis customization or chart type switching
// ---------------------------------------------------------------------------

const NON_MODIFIABLE_STYLES: ReadonlySet<ChartStyle> = new Set([
  'funnel',
  'sankey',
]);

export function isChartModifiable(style: ChartStyle | undefined): boolean {
  if (!style) {
    return false;
  }
  return !NON_MODIFIABLE_STYLES.has(style);
}

// ---------------------------------------------------------------------------
// Axis field extraction (handles x_axis as single object or array)
// ---------------------------------------------------------------------------

/**
 * Extracts x-axis fields as an array. The backend may send x_axis as either a
 * single ChartAxis object or an array of ChartAxis objects.
 */
export function getXAxisFields(
  config: ChartConfig | null | undefined,
): ChartAxis[] {
  if (!config?.x_axis) {
    return [];
  }
  const xAxis = config.x_axis;
  if (Array.isArray(xAxis)) {
    return xAxis;
  }
  return [xAxis];
}

/** Returns y-axis fields (always an array, empty if missing). */
export function getYAxisFields(
  config: ChartConfig | null | undefined,
): ChartSeries[] {
  return config?.y_axis ?? [];
}

// ---------------------------------------------------------------------------
// Chart type switching
// ---------------------------------------------------------------------------

/**
 * Returns the list of chart styles a user can switch to from the current style.
 * Switching is allowed only within the same kind group (categorical, proportion, flow).
 * Non-modifiable styles (funnel, sankey) are excluded from the result.
 */
export function getModifiableChartStyles(
  defaultStyle: ChartStyle | undefined,
): ChartStyle[] {
  if (!defaultStyle || !isChartModifiable(defaultStyle)) {
    return [];
  }
  // Flow charts (sankey) are non-modifiable, so this only applies to categorical/proportion.
  if (isFlowKind(defaultStyle)) {
    return [];
  }
  const group = getKindsInGroup(defaultStyle);
  return group.filter((s) => !NON_MODIFIABLE_STYLES.has(s));
}

// ---------------------------------------------------------------------------
// Default axis key selection
// ---------------------------------------------------------------------------

/**
 * Computes default axis key selections for a given chart config.
 * - x-axis: first field (single-select in all modifiable charts)
 * - y-axis: all fields (multi-select); empty for donut
 */
export function getDefaultAxisKeys(config: ChartConfig | null | undefined): {
  xAxisKeys: string[];
  yAxisKeys: string[];
} {
  const xFields = getXAxisFields(config);
  const yFields = getYAxisFields(config);

  return {
    xAxisKeys: xFields.length > 0 ? [xFields[0].key] : [],
    yAxisKeys: yFields.map((f) => f.key),
  };
}

// ---------------------------------------------------------------------------
// Resolved chart config
// ---------------------------------------------------------------------------

/**
 * Creates a resolved ChartConfig by filtering the full config down to only the
 * selected axis fields. The result has a single x_axis object (the selected
 * x-axis field) and a filtered y_axis array — exactly what buildCategoricalSeries
 * and buildProportionSeries expect.
 *
 * If selections are empty or don't match any fields, falls back to defaults.
 */
export function resolveChartConfig(
  config: ChartConfig | null | undefined,
  selectedXKeys: string[],
  selectedYKeys: string[],
): ChartConfig | null {
  if (!config) {
    return null;
  }

  const xFields = getXAxisFields(config);
  const yFields = getYAxisFields(config);

  // Resolve x-axis: find the selected field, fall back to first available
  const selectedX =
    xFields.find((f) => selectedXKeys.includes(f.key)) ??
    xFields[0] ??
    undefined;

  // Resolve y-axis: filter to selected keys, preserving original order.
  // Fall back to all fields if nothing matches (backward compat).
  let resolvedY: ChartSeries[];
  if (selectedYKeys.length > 0) {
    resolvedY = yFields.filter((f) => selectedYKeys.includes(f.key));
    if (resolvedY.length === 0) {
      resolvedY = yFields;
    }
  } else {
    resolvedY = yFields;
  }

  return {
    ...config,
    x_axis: selectedX,
    y_axis: resolvedY,
  };
}
